import { Routes, useParam } from "@blitzjs/next"
import { useQuery } from "@blitzjs/rpc"
import { enabled, Feature } from "app/core/features"
import { trpc } from "app/core/trpcClient"
import {
  createContext,
  FC,
  FunctionComponent,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
} from "react"
import { ApplicationPlatform } from "@superwall/db/constants"
import { SuperwallLastApplicationIdKey } from "./constants"
import { useRouter } from "next/router"
import getProjects from "app/projects/queries/getProjects"

type OnPlatformFunc = (platform: ApplicationPlatform, children: any) => JSX.Element | null

export type ApplicationContextType = {
  isWeb?: boolean
  applicationId: number
  publicApiKey?: string
  featuresEnabled: string[]
  enabled: (feature: Feature) => boolean
  platform?: ApplicationPlatform
  onPlatform: OnPlatformFunc
  // Project state: null = loading, undefined = no project, object = project loaded
  project: Awaited<ReturnType<typeof getProjects>>[number] | undefined | null
}

export const ApplicationContext = createContext<ApplicationContextType>({
  isWeb: false,
  applicationId: 0,
  featuresEnabled: [],
  enabled: () => {
    return false
  },
  onPlatform: () => null,
  project: null, // null indicates loading state
})
export const ApplicationContextProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
  const router = useRouter()
  // If this is undefined & we have `:applicationId` in the url, then we need to
  // select an application, then redirect
  const applicationIdString = useParam("applicationId", "string")!
  const applicationId = parseInt(applicationIdString, 10)
  const applicationIdIsValid = typeof applicationId === "number" && !isNaN(applicationId)
  const lastApplicationId =
    typeof localStorage != "undefined" ? localStorage.getItem(SuperwallLastApplicationIdKey) : ""
  const { data: application } = trpc.blitzMigration.applications.getApplication.useQuery(
    { id: applicationId },
    {
      enabled: !!applicationId && applicationIdIsValid,
      suspense: false,
      staleTime: Infinity,
      refetchOnMount: false,
    }
  )

  const [projects] = useQuery(
    getProjects,
    { applicationId, filterOnlyApplications: true },
    {
      enabled: !!applicationId && applicationIdIsValid,
      suspense: false,
      staleTime: Infinity,
      refetchOnMount: false,
    }
  )

  const project = useMemo(() => {
    // Projects are still loading
    if (projects === undefined) {
      return null
    }
    // Projects loaded but empty (no projects)
    if (projects.length === 0) {
      return undefined
    }
    // Projects loaded with data
    return projects[0]
  }, [projects])

  // Magic redirect handling
  useEffect(() => {
    if (typeof window === "undefined") {
      return
    }
    const path = window.location.pathname

    // Handle :applicationId or :app paths
    if (
      typeof applicationIdString === "undefined" &&
      (path.includes(":applicationId") || path.includes(":app"))
    ) {
      // Redirect to select application page
      // If there's one app, pick it, otherwise show the select app page
      // if (false && lastApplicationId && parseInt(lastApplicationId)) {
      //   const newURL = new URL(window.location.href)
      //   newURL.pathname = newURL.pathname.replace(":applicationId", lastApplicationId)
      //   router.push(newURL.toString())
      // } else {
      // Redirect to select application page
      router.push(
        Routes.SelectApplicationPage({
          pathname: window.location.pathname,
        })
      )
      return
    }
  }, [applicationIdString, lastApplicationId, router])

  // Write last application to local storage, used in
  // pages/applications/index.tsx
  useEffect(() => {
    try {
      if (applicationId && applicationIdIsValid) {
        localStorage.setItem(SuperwallLastApplicationIdKey, applicationId.toString())
      }
    } catch (error) {}
  }, [applicationId, applicationIdIsValid])

  return (
    <ApplicationContext.Provider
      value={{
        isWeb: application?.platform === ApplicationPlatform.WEB || false,
        applicationId,
        publicApiKey: application?.publicApiKey,
        featuresEnabled: (application?.featuresEnabled as string[]) || [],
        enabled: (feature) => {
          return enabled(feature, application) || false
        },
        platform: application?.platform,
        onPlatform: (platform, children: any) => {
          if (application?.platform === platform) {
            return <>{children}</>
          }
          return null
        },
        project,
      }}
    >
      {children}
    </ApplicationContext.Provider>
  )
}

export const useOrganizationId = () => {
  const { applicationId } = useApplicationContext()
  const { data: application } = trpc.blitzMigration.applications.getApplication.useQuery(
    { id: applicationId },
    { suspense: false }
  )
  return application?.organizationId
}

export const useDefiniteOrganizationId = () => {
  const organizationId = useOrganizationId()
  if (!organizationId) {
    throw new Error("Organization ID not available")
  }
  return organizationId
}

const Wrapper = ({ children }: PropsWithChildren<{}>) => {
  return <>{children}</>
}
Wrapper.displayName = "Wrapper"

export const whenOrganizationIdAvailable = <T,>(Comp: FunctionComponent<T>) => {
  Comp.displayName = "whenOrganizationIdAvailable"
  // eslint-disable-next-line react/display-name
  return (props: T) => {
    const organizationId = useOrganizationId()
    if (!organizationId) {
      return null
    }
    return (
      <Wrapper>
        {/* @ts-ignore */}
        <Comp {...props} />
      </Wrapper>
    )
  }
}

export const useApplicationContext = () => useContext(ApplicationContext)
